import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import "./_legal.css";

const LOGO_SRC = "/wot-logo-512.png";

export interface Section {
  id: string;
  title: string;
  body: ReactNode;
}

interface LegalLayoutProps {
  /** Current page key, used to mark the active sibling in the cross-link card row. */
  page: "terms" | "privacy" | "analytics";
  eyebrow: string;
  title: ReactNode;
  lede: ReactNode;
  /** ISO 8601 date for the "last updated" stamp. Rendered as a friendly date string. */
  updatedISO: string;
  sections: Section[];
}

/**
 * Shared layout for /terms, /privacy, /analytics.
 *
 * Layout:
 *   - Sticky top bar (Wordmark + meta links)
 *   - Hero (eyebrow / title / lede / updated stamp)
 *   - 2-column body: sticky TOC on desktop, content on right
 *   - Sibling-page card row at the bottom
 *   - Footer with cross-links + copyright
 *
 * Mobile (<900px): TOC collapses inline above the content as a chip
 * card; the body becomes a single column.
 */
export const LegalLayout = ({
  page, eyebrow, title, lede, updatedISO, sections,
}: LegalLayoutProps) => (
  <div className="legal-page">
    {/* Top bar */}
    <header className="lg-top">
      <div className="lg-top-inner">
        <Link to="/" className="lg-lockup" aria-label="Wise Owl Talks home">
          <span className="av"><img src={LOGO_SRC} alt="" /></span>
          <span className="lg-wordmark">Wise<span>Owl</span></span>
        </Link>
        <nav className="lg-top-meta">
          <Link to="/">Home</Link>
        </nav>
      </div>
    </header>

    <main className="lg-shell">
      {/* Hero */}
      <div className="lg-hero">
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p className="lede">{lede}</p>
        <div className="stamp">
          <span>Last updated</span>
          <span className="pill">{formatDate(updatedISO)}</span>
        </div>
      </div>

      {/* Body */}
      <div className="lg-body">
        <aside className="lg-toc" aria-label="Table of contents">
          <div className="title">On this page</div>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </aside>

        <div className="lg-content">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id}>
              <h2>
                <span className="n">{String(i + 1).padStart(2, "0")}</span>
                {s.title}
              </h2>
              {s.body}
            </section>
          ))}

          <div className="lg-siblings">
            <Link to="/terms" className={page === "terms" ? "current" : ""}>
              <span className="k">Legal</span>
              <span className="v">Plain terms</span>
              <span className="d">Account, content, billing, the "not therapy" disclaimer.</span>
            </Link>
            <Link to="/privacy" className={page === "privacy" ? "current" : ""}>
              <span className="k">Privacy</span>
              <span className="v">What we hold</span>
              <span className="d">What we collect, what we don't, and your rights.</span>
            </Link>
            <Link to="/analytics" className={page === "analytics" ? "current" : ""}>
              <span className="k">Transparency</span>
              <span className="v">How we measure</span>
              <span className="d">Pageviews and patterns — never your reflections.</span>
            </Link>
          </div>
        </div>
      </div>
    </main>

    <footer className="lg-foot">
      <div className="lg-foot-inner">
        <span>&copy; {new Date().getFullYear()} Wise Owl Talks · A wise pause in a noisy world</span>
        <div className="lg-foot-links">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/analytics">Analytics</Link>
          <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a>
        </div>
      </div>
    </footer>
  </div>
);

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};
